import type { User } from 'vince-core/types/users';
import type { SortDirection } from 'vince-core/types/table-generic-search';

export enum CommunicationRole {
    USER = 'USER',
    LLM = 'LLM',
    SYSTEM = 'SYSTEM'
}

export enum ResourceType {
    CUSTOM_TABLE = 'CUSTOM_TABLE',
    WORKFLOW = 'WORKFLOW',
    MCP_SERVER = 'MCP_SERVER'
}


export enum State {
    PUBLISHED = 'PUBLISHED',
    DRAFT = 'DRAFT',
    ARCHIVED = 'ARCHIVED'
}


export enum ApplicationType {
    GENERATED = 'GENERATED',
    PROVIDED = 'PROVIDED'
}


export interface VersionInfo {
    description?: string;
    state?: State;
    providedSourceUrl?: string;
}

export interface ApplicationInfo {
    id: string;
    name: string;
    description: string;
    labels?: string[];
}

export interface Auditable {
    created: string;
    modified: string;
    createdBy: string;
    modifiedBy: string;
    createdByUser?: User;
    modifiedByUser?: User;
}

export interface PublishedInfo {
    version: number;
    publishedAt: string;
    publishedBy: string;
}

export interface Application extends ApplicationInfo, Auditable {
    currentVersion?: number;
    isFavourite: boolean;
    currentVersionState: State;
    publishedInfo?: PublishedInfo;
}

export interface ApplicationsListQuery {
    filter?: string;
    sortDirection?: SortDirection;
    sort?: string;
    onlyFavourites?: boolean;
    authorId?: string;
    rowsPerPage?: number;
    page?: number;
}

export interface Resource {
    name: string;
    resourceId: string;
    type: ResourceType;
}

export interface CreateApplicationRequest {
    resources: Resource[];
    name: string;
    connectionId?: string;
    description: string;
    labels: [];
}

export interface CreateVersionRequest {
    source: ApplicationType;
    connectionId?: string;
    description: string;
    providedSourceUrl?: string;
    resources: Resource[];
    copyAssetsFromVersionId?: string;
    copyRolesFromVersionId?: string;
    copyRolesAssignments?: boolean;
}

export interface CopyApplicationRequest {
    name: string;
    versionId: string;
    description?: string;
    labels?: string[];
}

export interface CopyApplicationResponse {
    applicationId: string;
    versionId: string;
}

export interface ApplicationVersion extends Auditable {
    id: string;
    version: number;
    description?: string;
    state: State;
    stateChangeDate: string;
    source: ApplicationType;

    createdWithConnectionId?: string;
    providedSourceUrl?: string;
}

export interface CreateSessionResponse {
    sessionId: string;
}

export interface MessageAttachment {
    contentType: string;
    filename: string;
    size: number;
    content: string;
}

export interface SessionLog {
    role: CommunicationRole;
    content: string;
    date: string;
    attachments?: MessageAttachment[];
}

export interface ApplicationSession {
    sessionId: string;
    expiresAt: string;
    indexPage: string;
    applicationName: string;
    applicationDescription: string;
    versionDescription: string;
    version: number;
    resources: Resource[];
    roles: string[];
}

export interface Asset {
    id: string;
    storage: string;
    filename: string;
    contentType: string;
    size: number;
}

export interface ApplicationVersionDetails extends ApplicationVersion {
    resources: Resource[];
    assets: Asset[];
}


export enum ActionItemsStatus {
    NOT_STARTED = 'NOT_STARTED',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED'
}

export interface StructuredContent {
    type?: string;
}

export interface Plan extends StructuredContent {
    summary: string;
    actionItems: {
        id: string;
        actionDescription: string;
        filename?: string;
        status: ActionItemsStatus;
        notes?: string;
    }[];
}


export interface Message {
    role: CommunicationRole;
    content: string;
    structuredContent?: Plan;
    timestamp: Date;
    attachments?: MessageAttachment[];
    streaming?: boolean;
    cancelled?: boolean;
    undone?: boolean;
}


export enum ProcessUpdateEventType {
    RESPONSE_START = 'RESPONSE_START',
    RESPONSE_UPDATE = 'RESPONSE_UPDATE',
    RESPONSE_COMPLETE = 'RESPONSE_COMPLETE',
    ASSETS_UPDATE_START = 'ASSETS_UPDATE_START',
    ASSETS_UPDATE_COMPLETE = 'ASSETS_UPDATE_COMPLETE',
    MESSAGE_COMPLETE = 'MESSAGE_COMPLETE',
    PLAN_UPDATE = 'PLAN_UPDATE',
    ERROR = 'ERROR'
}

export interface ProcessUpdateEvent {
    sessionId: string;
    type: ProcessUpdateEventType;
    chunk: number;
    turnIndex?: number;
    structuredResponse?: Plan;
    textDelta?: string;
    error?: string;
}

export interface AssetDetails {
    name: string;
    content: string;
    contentType: string;
    contentBase64Encoded: boolean;
}

export interface UpdateAssetRequest {
    content: string;
    contentBase64Encoded: boolean;
}

export interface TenantPrompt extends Auditable {
    prompt: string;
}

export interface UpdateTenantPromptRequest {
    prompt: string;
}

export interface ApplicationRole extends Auditable {
    id?: string;
    role: string;
    description: string;
}

export enum Principal {
    USER = 'USER',
    ROLE = 'ROLE'
}

export interface RoleAssignment extends Partial<Auditable> {
    id?: string;
    principalType: Principal;
    principalId: string;
}

export interface UpdateRoleAssignmentsRequest {
    addedAssignments: RoleAssignment[];
    removedAssignmentsIds: string[];
}

interface SessionBuildLogMessage {
    text: string;
    role: CommunicationRole;
    attachments: MessageAttachment[];
}

export interface SessionBuildLog {
    id: string;
    userId: string;
    created: string;
    completed: string;
    prompt: SessionBuildLogMessage[];
}

export interface Feedback {
    id: string;
    applicationId: string;
    versionId: string;
    comment: string;
    created: string;
    createdBy: string;
    readAt?: string | null;
    readBy?: string | null;
}

export enum FormFactor {
    DESKTOP = 'DESKTOP',
    TABLET = 'TABLET',
    HANDHELD = 'HANDHELD'
}

export interface PreviewDevice {
    id: string,
    label: string,
    formFactor: FormFactor,
    screenWidthPx: number,
    screenHeightPx?: number
    createdBy: string,
    created: string
}

export interface CreatePreviewDeviceRequest {
    label: string,
    formFactor: FormFactor,
    screenWidthPx: number,
    screenHeightPx?: number
}

export interface McpServerBase {
    name: string;
    systemPrompt?: string;
    config: string;
}

export interface McpServer extends Auditable, McpServerBase {
    id: string;
}

export type CreateMcpServerRequest = McpServerBase;

export type UpdateMcpServerRequest = McpServerBase;

export interface ProbeMcpServerRequest {
    config: string;
}

export interface McpProbeServerInfo {
    name?: string;
    version?: string;
}

export interface McpProbeTool {
    name: string;
    description?: string;
}

export interface McpProbeResult {
    reachable: boolean;
    serverInfo?: McpProbeServerInfo;
    tools?: McpProbeTool[];
    error?: string;
}

export interface EmulatorDimensions {
    screenWidthPx: number;
    screenHeightPx: number;
}

export type SaveMode = 'continue' | 'exit';

export interface SaveDropdownHandle {
    toggle: (event: Event) => void;
    hide: () => void;
}

